
import React, { useState, useEffect, useRef } from 'react';
import './CartPage.css';
import { useApp } from '../../context/AppContext';
import { ordersAPI } from '../../services/api';

/**
 * Cart + Checkout page.
 *
 * Flow:
 *   1. Items live in AppContext (cart). User adjusts qty / removes.
 *   2. "Proceed to Checkout" reveals shipping picker (if any saved) +
 *      PayPal Smart Buttons.
 *   3. PayPal createOrder() is server-driven: we POST to /api/orders to
 *      create a pending order row in D1, then hand the dollar total to
 *      PayPal client-side so the button shows the real price.
 *   4. On approve, PayPal returns a paypalOrderId; we POST it to
 *      /api/orders/capture which flips status='paid' in D1 and stores
 *      the capture id. Cart clears, success screen shows.
 *
 * We use PayPal Smart Buttons inline rather than the redirect approval
 * flow because Smart Buttons are SPA-friendly and don't require a
 * backend PayPal API integration (orders.ts is just a ledger; PayPal
 * handles the actual money movement).
 */

declare global {
  interface Window {
    // Loaded by <script src="...paypal.com/sdk/js..."> in index.html.
    // Namespaced via data-namespace="paypal_sdk" so it doesn't clobber
    // any other window.paypal that might exist.
    paypal_sdk?: any;
    paypal?: any;
  }
}

const CartPage: React.FC = () => {
  const { cart, products, cartTotal, updateCartQuantity, removeFromCart, clearCart, currentUser, setActiveTab } = useApp();
  const [showCheckout, setShowCheckout] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(currentUser.shippingAddresses[0]?.id || '');
  const [processing, setProcessing] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);
  const [completedOrderId, setCompletedOrderId] = useState<string | null>(null);
  const [paypalReady, setPaypalReady] = useState(false);
  const paypalContainerRef = useRef<HTMLDivElement | null>(null);
  const paypalButtonsRendered = useRef(false);

  // Match server math (orders.ts): flat $9.99 shipping, 8% tax.
  const shipping = cart.length > 0 ? 9.99 : 0;
  const tax = cartTotal * 0.08;
  const total = cartTotal + shipping + tax;

  const cartItems = cart
    .map(item => {
      const product = products.find(p => p.id === item.productId);
      return product ? { ...item, product } : null;
    })
    .filter((x): x is NonNullable<typeof x> => x !== null);

  // Wait for the PayPal SDK to finish loading. The script tag uses
  // `defer`, so window.paypal_sdk may not be ready on first mount.
  useEffect(() => {
    if (window.paypal_sdk || window.paypal) {
      setPaypalReady(true);
      return;
    }
    let cancelled = false;
    const interval = setInterval(() => {
      if (cancelled) return;
      if (window.paypal_sdk || window.paypal) {
        setPaypalReady(true);
        clearInterval(interval);
      }
    }, 200);
    // Give up after 10s — surfacing a graceful error is better than spinning.
    const timeout = setTimeout(() => { cancelled = true; clearInterval(interval); }, 10000);
    return () => { cancelled = true; clearInterval(interval); clearTimeout(timeout); };
  }, []);

  // Render PayPal Smart Buttons inside the checkout panel. Re-render
  // when total changes so the captured amount is always current.
  useEffect(() => {
    if (!showCheckout) return;
    if (!paypalReady) return;
    if (cart.length === 0) return;
    const paypal = window.paypal_sdk || window.paypal;
    if (!paypal?.Buttons || !paypalContainerRef.current) return;

    // Clear any prior render so re-running the effect doesn't stack
    // buttons on top of each other.
    paypalContainerRef.current.innerHTML = '';
    paypalButtonsRendered.current = false;

    // Single render path — actions-based Smart Buttons. The previously
    // duplicated "first attempt" Buttons() call above this point was
    // removed 2026-05-17: it constructed a Buttons instance without
    // calling .render(), but on cart mutations the deps fired a tear-
    // down + rebuild that risked stacking buttons on flaky SDK loads.
    paypal.Buttons({
      style: { layout: 'vertical', color: 'gold', shape: 'rect', label: 'paypal' },
      createOrder: async (_data: any, actions: any) => {
        setOrderError(null);
        try {
          const items = cart.map(item => ({
            productId: item.productId,
            quantity: item.quantity,
            size: item.size,
          }));
          const result = await ordersAPI.create(items, selectedAddress) as any;
          if (!result?.success || !result.orderId) {
            throw new Error(result?.error || 'Could not create order');
          }
          sessionStorage.setItem('pending_order_id', result.orderId);
          const amount = result.amount || total.toFixed(2);
          return actions.order.create({
            intent: 'CAPTURE',
            purchase_units: [
              {
                custom_id: result.orderId, // ties PayPal txn back to our order row
                amount: { currency_code: 'USD', value: amount },
                description: cartItems
                  .map(i => `${i.product.name} × ${i.quantity}`)
                  .join(', ')
                  .slice(0, 127),
              },
            ],
          });
        } catch (e: any) {
          setOrderError(e?.message || 'Could not start checkout');
          throw e;
        }
      },
      onApprove: async (_data: any, actions: any) => {
        setProcessing(true);
        try {
          const details = await actions.order.capture();
          const paypalOrderId = details?.id;
          const captureId = details?.purchase_units?.[0]?.payments?.captures?.[0]?.id;
          const orderId = sessionStorage.getItem('pending_order_id');
          if (!orderId) throw new Error('Missing local order id');
          const res = await ordersAPI.capture(orderId, paypalOrderId, captureId) as any;
          if (res?.success) {
            sessionStorage.removeItem('pending_order_id');
            clearCart();
            setCompletedOrderId(orderId);
            setOrderComplete(true);
          } else {
            setOrderError('Payment captured but order update failed. Contact support.');
          }
        } catch (e: any) {
          console.error('PayPal capture failed:', e);
          setOrderError(e?.message || 'Payment failed during capture');
        } finally {
          setProcessing(false);
        }
      },
      onError: (err: any) => {
        console.error('PayPal Buttons error:', err);
        setOrderError('PayPal error — please try again.');
      },
      onCancel: () => {
        setOrderError(null);
      },
    }).render(paypalContainerRef.current).then(() => {
      paypalButtonsRendered.current = true;
    }).catch((err: any) => {
      console.error('Failed to render PayPal Buttons:', err);
      setOrderError('Could not load PayPal — refresh the page and try again.');
    });
    // Cleanup: clearing the container on dep change is enough; PayPal
    // doesn't expose an explicit teardown.
  }, [showCheckout, paypalReady, cart, total, selectedAddress, cartItems, clearCart]);

  if (orderComplete) {
    return (
      <div className="cart-page">
        <div className="order-success animate-scale-in">
          <div className="success-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
            </svg>
          </div>
          <h2>Order Confirmed!</h2>
          {completedOrderId && (
            <p className="order-number">Order #{completedOrderId.slice(-6).toUpperCase()}</p>
          )}
          <p>Thank you for your purchase! You'll receive email updates as your order ships.</p>
          <div className="success-actions">
            <button className="continue-btn" onClick={() => { setOrderComplete(false); setShowCheckout(false); setActiveTab('shop'); }}>
              Continue Shopping
            </button>
            <button className="view-orders-btn" onClick={() => { setOrderComplete(false); setShowCheckout(false); setActiveTab('profile'); }}>
              View My Orders
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (cart.length === 0) {
    return (
      <div className="cart-page">
        <div className="empty-cart">
          <div className="empty-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="9" cy="21" r="1"/>
              <circle cx="20" cy="21" r="1"/>
              <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/>
            </svg>
          </div>
          <h2>Your cart is empty</h2>
          <p>Discover amazing products in our shop!</p>
          <button className="shop-now-btn" onClick={() => setActiveTab('shop')}>
            Shop Now
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page">
      {!showCheckout ? (
        <>
          <div className="cart-items">
            {cartItems.map(item => (
              <div key={`${item.productId}-${item.size}`} className="cart-item animate-fade-in">
                <img src={item.product.images[0]} alt={item.product.name} className="item-image" />
                <div className="item-details">
                  <h3>{item.product.name}</h3>
                  <p className="item-size">Size: {item.size}</p>
                  <p className="item-price">${(item.product.salePrice || item.product.price).toFixed(2)}</p>
                </div>
                <div className="item-actions">
                  <div className="quantity-control">
                    <button onClick={() => updateCartQuantity(item.productId, item.size, item.quantity - 1)}>-</button>
                    <span>{item.quantity}</span>
                    <button onClick={() => updateCartQuantity(item.productId, item.size, item.quantity + 1)}>+</button>
                  </div>
                  <button className="remove-btn" onClick={() => removeFromCart(item.productId, item.size)}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="cart-summary">
            <div className="summary-row">
              <span>Subtotal</span>
              <span>${cartTotal.toFixed(2)}</span>
            </div>
            <div className="summary-row">
              <span>Shipping</span>
              <span>${shipping.toFixed(2)}</span>
            </div>
            <div className="summary-row">
              <span>Tax</span>
              <span>${tax.toFixed(2)}</span>
            </div>
            <div className="summary-row total">
              <span>Total</span>
              <span>${total.toFixed(2)}</span>
            </div>
            <button className="checkout-btn" onClick={() => setShowCheckout(true)}>
              Proceed to Checkout
            </button>
            <button className="clear-btn" onClick={clearCart}>Clear Cart</button>
          </div>
        </>
      ) : (
        <div className="checkout-section animate-fade-in">
          <h2>Checkout</h2>

          {currentUser.shippingAddresses.length > 0 && (
            <div className="checkout-field">
              <h3>Shipping Address</h3>
              <div className="address-options">
                {currentUser.shippingAddresses.map(addr => (
                  <label key={addr.id} className={`address-option ${selectedAddress === addr.id ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="address"
                      value={addr.id}
                      checked={selectedAddress === addr.id}
                      onChange={() => setSelectedAddress(addr.id)}
                    />
                    <div className="address-content">
                      <strong>{addr.name}</strong>
                      <p>{addr.street}</p>
                      <p>{addr.city}, {addr.state} {addr.zip}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="order-summary">
            <h3>Order Summary</h3>
            {cartItems.map(item => (
              <div key={`${item.productId}-${item.size}`} className="summary-item">
                <span>{item.product.name} × {item.quantity}</span>
                <span>${((item.product.salePrice || item.product.price) * item.quantity).toFixed(2)}</span>
              </div>
            ))}
            <div className="summary-divider" />
            <div className="summary-item">
              <span>Subtotal</span>
              <span>${cartTotal.toFixed(2)}</span>
            </div>
            <div className="summary-item">
              <span>Shipping</span>
              <span>${shipping.toFixed(2)}</span>
            </div>
            <div className="summary-item">
              <span>Tax</span>
              <span>${tax.toFixed(2)}</span>
            </div>
            <div className="summary-item total">
              <span>Total</span>
              <span>${total.toFixed(2)}</span>
            </div>
          </div>

          {orderError && (
            <div className="checkout-error">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
              </svg>
              <span>{orderError}</span>
            </div>
          )}

          {/* PayPal Smart Buttons mount point.
              Stays visible even while processing so the user gets
              feedback if the SDK is slow to load. */}
          <div className="paypal-buttons-mount">
            {!paypalReady && (
              <div className="paypal-loading">
                <span className="spinner" />
                <span>Loading PayPal…</span>
              </div>
            )}
            <div ref={paypalContainerRef} aria-label="PayPal payment buttons" />
            {processing && (
              <div className="paypal-loading">
                <span className="spinner" />
                <span>Finalizing your order…</span>
              </div>
            )}
          </div>

          <p className="secure-payment">
            <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
              <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
            </svg>
            Secure checkout powered by PayPal
          </p>

          <button className="back-btn" onClick={() => setShowCheckout(false)}>
            ← Back to Cart
          </button>
        </div>
      )}
    </div>
  );
};

export default CartPage;
